// Browser-native text-to-speech (window.speechSynthesis) -- free, works
// offline, no API key or network round trip. Shared by every voice-guided
// flow in the app (incident Q&A, guided damage photos) so they all get the
// same "pick a less robotic voice" behaviour for free.

// The browser's voice list often comes back empty the first time it's asked
// -- most browsers load voices asynchronously and only fire "voiceschanged"
// once they're ready. A short wait for that event (capped so a browser that
// never fires it can't hang the flow) gets the real list instead of
// whatever was available on the very first tick.
function getVoices(): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    const existing = window.speechSynthesis.getVoices();
    if (existing.length > 0) {
      resolve(existing);
      return;
    }
    const timeout = setTimeout(() => resolve(window.speechSynthesis.getVoices()), 300);
    window.speechSynthesis.onvoiceschanged = () => {
      clearTimeout(timeout);
      resolve(window.speechSynthesis.getVoices());
    };
  });
}

// The default voice most browsers fall back to reads flat and robotic.
// Most platforms also ship at least one noticeably better one -- Chrome/
// Android's Google "Natural" voices, Edge's "Online (Natural)" voices, and
// iOS Safari's Siri voices -- picked out here by the markers each platform
// actually uses in its voice names. Falls back to any local English voice,
// then to the browser's own default (undefined -- SpeechSynthesisUtterance
// uses its built-in default voice when none is set).
let cachedVoice: SpeechSynthesisVoice | null | undefined;
async function pickVoice(): Promise<SpeechSynthesisVoice | undefined> {
  if (cachedVoice !== undefined) return cachedVoice ?? undefined;
  try {
    const voices = await getVoices();
    const english = voices.filter((v) => v.lang.toLowerCase().startsWith("en"));
    const pool = english.length > 0 ? english : voices;
    const qualityMarkers = ["natural", "enhanced", "premium", "online"];
    const best =
      pool.find((v) => qualityMarkers.some((m) => v.name.toLowerCase().includes(m))) ||
      pool.find((v) => v.localService) ||
      pool[0];
    cachedVoice = best || null;
    return cachedVoice ?? undefined;
  } catch {
    cachedVoice = null;
    return undefined;
  }
}

export function speak(text: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      if (!("speechSynthesis" in window)) {
        resolve();
        return;
      }
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.rate = 0.95;
      utter.onend = () => resolve();
      utter.onerror = () => resolve();
      void pickVoice().then((voice) => {
        if (voice) utter.voice = voice;
        window.speechSynthesis.speak(utter);
      });
    } catch {
      resolve();
    }
  });
}
